import React, { useEffect } from 'react';
import PropertyHeader from './PropertyHeader';
import PropertyTabs from './PropertyTabs';
import { PropertyFilterProvider, usePropertyFilters } from '../../contexts/PropertyFilterContext';

/**
 * Componente interno que acessa o contexto de filtros e reseta quando necessário
 */
const PropertyViewInner = ({
  propertyData,
  _onZoomToGeometry,
  _onToggleLayerVisibility,
  onHighlightGeometry,
  _onApplyStyle,
  handleDomainClick,
  handleTabChange,
}) => {
  // Acesso ao contexto de filtros para resetar quando uma nova propriedade é carregada
  const { setSelectedCarCodes, setSelectedSigefCodes } = usePropertyFilters();

  // Efeito para resetar os filtros quando uma nova propriedade é carregada
  useEffect(() => {
    if (propertyData) {
      //console.log('[PropertyView] Nova propriedade carregada, resetando filtros de seleção');
      // Resetar os filtros de seleção para garantir que não permaneçam de uma propriedade anterior
      setSelectedCarCodes([]);
      setSelectedSigefCodes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyData?._metadata?.file_id, setSelectedCarCodes, setSelectedSigefCodes]);

  return (
    <div className="property-view flex flex-row gap-4">
      {/* Painel lateral: catálogo de funções */}
      <div style={{ minWidth: 0, maxWidth: 0 }}>
        <h4 className="text-sm font-semibold mb-2"></h4>
        {/* Import dinâmico para evitar erro se não existir MUI */}
        {(() => {
          try {
            const { useFunctionCatalog } = require('../../hooks/useFunctionCatalog');
            const { FunctionCatalogChips } = require('../FunctionCatalogChips');
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const { catalog, loading, error } = useFunctionCatalog();
            if (loading) return <div></div>;
            if (error) return <div>E</div>;
            if (!catalog) return <div></div>;
            return (
              <FunctionCatalogChips
                catalog={catalog}
                onSelect={(fn) =>
                  alert(
                    `Função selecionada: ${fn.label}\n(implemente integração com textbox de regra aqui)`,
                  )
                }
              />
            );
          } catch {
            return <div></div>;
          }
        })()}
      </div>
      {/* Conteúdo principal da propriedade */}
      <div style={{ flex: 1 }}>
        <PropertyHeader propertyData={propertyData} onDomainClick={handleDomainClick} />
        <PropertyTabs
          propertyData={propertyData}
          onHighlightGeometry={onHighlightGeometry}
          onTabChange={handleTabChange}
        />
      </div>
    </div>
  );
};

/**
 * Componente principal que integra todos os componentes da visualização consolidada
 * de propriedades rurais, consumindo diretamente os dados JSON do backend.
 */
const PropertyView = ({
  propertyData,
  onZoomToGeometry,
  onToggleLayerVisibility,
  onHighlightGeometry,
  onApplyStyle,
}) => {
  //console.log('[PropertyView] Recebendo dados:', {
  //  dadosOriginais: !!propertyData,
  //  chavesPrincipais: propertyData ? Object.keys(propertyData) : []
  //});

  // Ref para armazenar a função que será usada para comunicar com as tabs
  const tabControlRef = React.useRef({
    openDomainTab: (_domain) => {
      //console.log('[PropertyView] Chamada de openDomainTab ignorada: função ainda não inicializada');
    },
  });

  // Handler que será passado para o PropertyTabs e receberá a função openDomainTab
  const handleTabChange = (_selectedTab) => {
    //console.log(`[PropertyView] Tab alterada para: ${selectedTab}`);
  };

  // Garantimos que o objeto já esteja inicializado com a função openDomainTab vazia
  // Isso evita problemas de sincronização quando o PropertyHeader tenta acessar antes da definição
  // eslint-disable-next-line react-hooks/immutability
  handleTabChange.openDomainTab = (_domain) => {
    //console.log(`[PropertyView] Função openDomainTab temporária chamada para: ${domain}`);
  };

  // Definimos o handler que será passado para o PropertyHeader
  const handleDomainClick = (domain) => {
    //console.log(`[PropertyView] Solicitando abertura da tab: ${domain}`);
    if (tabControlRef.current && tabControlRef.current.openDomainTab) {
      tabControlRef.current.openDomainTab(domain);
    }
  };

  // Quando o componente renderizar, preparamos o ref para receber a função do PropertyTabs
  useEffect(() => {
    // Atualizamos o ref para que seja acessível pelo PropertyTabs
    tabControlRef.current = {
      // Propriedade que será substituida pela função real do PropertyTabs
      openDomainTab: (domain) => {
        //console.log(`[PropertyView] openDomainTab chamado via ref para: ${domain}`);
        // Usamos a função recebida do PropertyTabs se existir
        if (handleTabChange.openDomainTab) {
          handleTabChange.openDomainTab(domain);
        }
      },
    };
  }, []);

  if (!propertyData) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm text-center">
        <p className="text-gray-500">Nenhum dado disponível para esta propriedade.</p>
      </div>
    );
  }

  return (
    <PropertyFilterProvider>
      <PropertyViewInner
        propertyData={propertyData}
        onZoomToGeometry={onZoomToGeometry}
        onToggleLayerVisibility={onToggleLayerVisibility}
        onHighlightGeometry={onHighlightGeometry}
        onApplyStyle={onApplyStyle}
        handleDomainClick={handleDomainClick}
        handleTabChange={handleTabChange}
      />
    </PropertyFilterProvider>
  );
};

export default PropertyView;
